import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import TCBadge from "@/components/TCBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import axios from "axios";

import { API_URL as API } from "@/lib/api";
const COLORS = ['#0284c7', '#0d9488', '#d97706', '#7c3aed', '#e11d48', '#059669', '#ea580c', '#6366f1', '#be185d', '#0891b2'];

const FamiliaSelect = ({ value, onChange, familias, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-7 w-40 text-xs" data-testid={testId}>
      <SelectValue placeholder="General" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">General</SelectItem>
      {familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
    </SelectContent>
  </Select>
);

const SalidasKPI = ({ globalTC }) => {
  const [entregas, setEntregas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [famValorMes, setFamValorMes] = useState("__all__");
  const [famTipoVenta, setFamTipoVenta] = useState("__all__");
  const [famDestinos, setFamDestinos] = useState("__all__");
  const [famFamilia, setFamFamilia] = useState("__all__");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, fRes] = await Promise.all([
        axios.get(`${API}/api/entregas/all`),
        axios.get(`${API}/api/entregas/filters`),
      ]);
      setEntregas(eRes.data.entregas || []);
      setFamilias(fRes.data.familias || []);
    } catch {
      toast.error("Error al cargar datos KPI");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ff = (arr, fam) => fam === "__all__" ? arr : arr.filter(x => x.familia === fam);

  // 1. Valor de salidas por mes
  const valorPorMes = useMemo(() => {
    const e = ff(entregas, famValorMes);
    const map = {};
    e.forEach(x => {
      if (!x.fecha) return;
      const month = x.fecha.substring(0, 7);
      map[month] = (map[month] || 0) + (x.cantidad || 0) * (x.pmayor || 0);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([mes, valor]) => ({ mes, valor: Math.round(valor) }));
  }, [entregas, famValorMes]);

  // 2. Distribución por tipo de venta
  const porTipoVenta = useMemo(() => {
    const e = ff(entregas, famTipoVenta);
    const map = {};
    e.forEach(x => { const t = x.tipo_venta || 'Sin Tipo'; map[t] = (map[t] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [entregas, famTipoVenta]);

  // 3. Top 10 destinos
  const topDestinos = useMemo(() => {
    const e = ff(entregas, famDestinos);
    const map = {};
    e.forEach(x => { if (x.destino) map[x.destino] = (map[x.destino] || 0) + (x.cantidad || 0); });
    return Object.entries(map).map(([destino, cantidad]) => ({ destino, cantidad })).sort((a, b) => b.cantidad - a.cantidad).slice(0, 10);
  }, [entregas, famDestinos]);

  // 4. Salidas por familia (or subfamilia when filtered)
  const porFamilia = useMemo(() => {
    const e = ff(entregas, famFamilia);
    const map = {};
    e.forEach(x => {
      const key = famFamilia !== "__all__" ? (x.subfamilia || "Sin SubFamilia") : (x.familia || "Sin Familia");
      map[key] = (map[key] || 0) + (x.cantidad || 0);
    });
    return Object.entries(map).map(([familia, cantidad]) => ({ familia, cantidad })).sort((a, b) => b.cantidad - a.cantidad).slice(0, 12);
  }, [entregas, famFamilia]);

  if (loading) {
    return <Card className="p-8 text-center border border-border"><p className="text-muted-foreground">Cargando KPIs...</p></Card>;
  }

  return (
    <div className="space-y-4" data-testid="salidas-kpi">
      {/* TC Badge */}
      {globalTC !== null && globalTC !== undefined && (
        <div className="flex justify-end mb-1"><TCBadge tc={globalTC} /></div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Valor por Mes */}
        <Card className="p-4 border border-border shadow-sm" data-testid="kpi-valor-mes">
          <div className="flex justify-between items-center mb-3">
            <h3 className="heading-font text-base font-semibold text-foreground">Valor de Salidas por Mes</h3>
            <FamiliaSelect value={famValorMes} onChange={setFamValorMes} familias={familias} testId="kpi-fam-valor-mes" />
          </div>
          {valorPorMes.length === 0 ? <p className="text-center text-xs text-muted-foreground py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={valorPorMes}>
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`${v.toLocaleString()} Bs`, 'Valor']} />
                <Bar dataKey="valor" fill="#0284c7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Tipo de Venta */}
        <Card className="p-4 border border-border shadow-sm" data-testid="kpi-tipo-venta">
          <div className="flex justify-between items-center mb-3">
            <h3 className="heading-font text-base font-semibold text-foreground">Distribucion por Tipo de Venta</h3>
            <FamiliaSelect value={famTipoVenta} onChange={setFamTipoVenta} familias={familias} testId="kpi-fam-tipo-venta" />
          </div>
          {porTipoVenta.length === 0 ? <p className="text-center text-xs text-muted-foreground py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={porTipoVenta} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                  {porTipoVenta.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Top Destinos */}
        <Card className="p-4 border border-border shadow-sm" data-testid="kpi-top-destinos">
          <div className="flex justify-between items-center mb-3">
            <h3 className="heading-font text-base font-semibold text-foreground">Top 10 Destinos por Cantidad</h3>
            <FamiliaSelect value={famDestinos} onChange={setFamDestinos} familias={familias} testId="kpi-fam-destinos" />
          </div>
          {topDestinos.length === 0 ? <p className="text-center text-xs text-muted-foreground py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topDestinos} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="destino" width={120} tick={{ fontSize: 10 }} />
                <Tooltip /><Bar dataKey="cantidad" fill="#0d9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Por Familia */}
        <Card className="p-4 border border-border shadow-sm" data-testid="kpi-por-familia">
          <div className="flex justify-between items-center mb-3">
            <h3 className="heading-font text-base font-semibold text-foreground">
              {famFamilia === "__all__" ? "Salidas por Familia (Top 12)" : `SubFamilias de ${famFamilia}`}
            </h3>
            <FamiliaSelect value={famFamilia} onChange={setFamFamilia} familias={familias} testId="kpi-fam-familia" />
          </div>
          {porFamilia.length === 0 ? <p className="text-center text-xs text-muted-foreground py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={porFamilia} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="familia" width={140} tick={{ fontSize: 10 }} />
                <Tooltip /><Bar dataKey="cantidad" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SalidasKPI;
